import { useEffect, useId, useRef, useState } from "react";
import styles from "./MultiSelect.module.css";
import { Checkbox } from "./Checkbox.js";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  /** Names the control, for the trigger and the panel it opens. */
  label: string;
  /** The current selection in words — what the closed control reads. */
  summary: string;
  options: MultiSelectOption[];
  /** **Empty means everything**, which is what `allLabel` offers a way back to. */
  selected: string[];
  onChange: (next: string[]) => void;
  allLabel: string;
}

/**
 * Choosing several of something, where the platform offers nothing usable.
 *
 * `<select multiple>` is the one native control that is worse than a built one:
 * it needs a modifier key nobody discovers, and on a phone it is a scrolling
 * box that fights the page. So this is a button and a panel of real checkboxes
 * — each one a native control with its own label, which is where the
 * accessibility actually comes from.
 */
export function MultiSelect({
  label,
  summary,
  options,
  selected,
  onChange,
  allLabel,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    /* Escape closes it and puts focus back, because a panel that closes and
       leaves focus on nothing strands a keyboard halfway down the page. */
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      trigger.current?.focus();
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  /**
   * Unticking the last one means every option rather than none. "Show me
   * nothing" is not a thing anyone opens a filter to ask for, and a screen
   * with every figure at zero is a worse answer than the one they started with.
   */
  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((current) => current !== value)
        : [...selected, value],
    );
  };

  return (
    <div className={styles.wrapper} ref={wrapper}>
      <button
        type="button"
        ref={trigger}
        className={styles.trigger}
        // The visible text is the value, so the name has to carry both — what
        // this control is, and what it is currently set to.
        aria-label={`${label}: ${summary}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <span className={styles.summary}>{summary}</span>
        <svg className={styles.chevron} viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M4 6l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div id={panelId} role="group" aria-label={label} className={styles.panel}>
          <Checkbox
            className={styles.option}
            checked={selected.length === 0}
            onChange={() => onChange([])}
            label={allLabel}
          />
          {options.map((option) => (
            <Checkbox
              key={option.value}
              className={styles.option}
              checked={selected.includes(option.value)}
              onChange={() => toggle(option.value)}
              label={option.label}
            />
          ))}
        </div>
      )}
    </div>
  );
}
